import React, { useEffect, useMemo, useState } from 'react';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import api from '../../network/ApiClient';
import { PLACEHOLDER_ID } from '../../helpers/placeholder';

export const ALL_MERCHANTS = 'all';

// Shared client-side merchant filter for admin list pages.
//
// Every admin list endpoint is already scoped to the caller's merchant
// memberships server-side (see kudukey-kb/merchant-scoping.md) and returns an
// `organizergroup` id on each row, so this filters the already-fetched rows in
// the browser. It does NOT send a `merchant=` query param — only /ride supports
// one (RIDES_API.md).
//
// Whether the dropdown shows, and what its options are, comes from the caller's
// MEMBERSHIP list (GET /organizergroup) — the same rule the routes calendar
// uses. It shows when the caller belongs to more than one merchant, and offers
// every merchant they belong to (even one that has no rows yet), rather than
// only the merchants that happen to appear in the current rows.
//
// `shared` lists merchant ids that are visible to every merchant (e.g. the
// Placeholder merchant 100001 whose locations the backend unions into every
// caller's list). They're never an option, don't count toward the ">1 merchant"
// trigger, and stay visible whatever merchant is selected.
//
// `merchantIds(row)` returns the merchant id(s) a row belongs to — a single id
// or an array (a user is many-to-many with merchants, so it returns the full
// `organizergroups` membership). Defaults to the row's `organizergroup`.
export function useMerchantFilter(rows, { shared = [], merchantIds } = {}) {
  const [merchantList, setMerchantList] = useState([]);
  const [selected, setSelected] = useState(ALL_MERCHANTS);

  useEffect(() => {
    let cancelled = false;
    api
      .get('organizergroup', { params: { simple: 'ok' } })
      .then((res) => {
        if (!cancelled) setMerchantList(res.data?.organizer || []);
      })
      .catch((e) => console.error('Unable to load merchants', e));
    return () => {
      cancelled = true;
    };
  }, []);

  const sharedSet = useMemo(() => new Set(shared.map(String)), [shared]);

  // The caller's merchants are the dropdown options and the source of truth for
  // "does this user belong to more than one merchant". Exclude the placeholder
  // merchant (never a real filter target) and any shared ids.
  const merchants = useMemo(
    () =>
      (merchantList || []).filter((m) => Number(m.id) !== PLACEHOLDER_ID && !sharedSet.has(String(m.id))),
    [merchantList, sharedSet]
  );

  const nameById = useMemo(() => {
    const map = {};
    (merchantList || []).forEach((m) => {
      map[m.id] = m.name;
    });
    return map;
  }, [merchantList]);

  const showFilter = merchants.length > 1;

  const idsOf = (row) => {
    const raw = merchantIds ? merchantIds(row) : row?.organizergroup;
    if (raw == null) return [];
    return (Array.isArray(raw) ? raw : [raw]).filter((v) => v != null);
  };

  // Filter rows to the selected merchant. Shared merchants always pass through,
  // and a row matches if any of its merchants is the selected one.
  const applyFilter = (input) => {
    if (!showFilter || selected === ALL_MERCHANTS) return input || [];
    const sel = String(selected);
    return (input || []).filter((row) => {
      const keys = idsOf(row).map(String);
      return keys.includes(sel) || keys.some((k) => sharedSet.has(k));
    });
  };

  return { merchants, selected, setSelected, showFilter, applyFilter, nameById };
}

export function MerchantFilterSelect({ merchants, value, onChange, showFilter, sx }) {
  if (!showFilter) return null;
  return (
    <FormControl size="small" sx={{ minWidth: 220, ...sx }}>
      <InputLabel id="merchant-filter-label">Merchant</InputLabel>
      <Select
        labelId="merchant-filter-label"
        label="Merchant"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <MenuItem value={ALL_MERCHANTS}>All</MenuItem>
        {merchants.map((m) => (
          <MenuItem key={m.id} value={m.id}>
            {m.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
